import { AlertCircle, Check, CloudOff, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import type { AutosaveStatus as Status } from "@/hooks/useAutosave";
import { cn } from "@/lib/utils";

interface AutosaveStatusProps {
  status: Status;
  onRetry?: () => void;
  /** Edits from an earlier visit were restored (JUB-152): say so and offer to discard them. */
  restored?: boolean;
  onDiscard?: () => void;
  /** Renders the secondary "Save now" button (calls flush). */
  onSaveNow?: () => void;
  className?: string;
}

/**
 * JUB-152: shows what happened to the user's edits — saving, saved, invalid,
 * error (with an explicit retry) or offline. Announced politely to screen
 * readers; autosaves never raise toasts.
 */
export function AutosaveStatus({ status, onRetry, restored, onDiscard, onSaveNow, className }: AutosaveStatusProps) {
  const { t } = useTranslation();

  const chip = (() => {
    switch (status) {
      case "saving":
        return (
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {t("autosave.saving")}
          </span>
        );
      case "saved":
        return (
          <span className="inline-flex items-center gap-1.5 text-green-700 dark:text-green-400">
            <Check className="h-4 w-4" aria-hidden />
            {t("autosave.saved")}
          </span>
        );
      case "pending":
        return <span className="text-muted-foreground">{t("autosave.pending")}</span>;
      case "invalid":
        return (
          <span className="inline-flex items-center gap-1.5 text-destructive">
            <AlertCircle className="h-4 w-4" aria-hidden />
            {t("autosave.invalid")}
          </span>
        );
      case "error":
        return (
          <span className="inline-flex items-center gap-1.5 text-destructive">
            <AlertCircle className="h-4 w-4" aria-hidden />
            {restored ? t("autosave.restoredDraft") : t("autosave.error")}
            {onRetry && (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0 text-destructive underline"
                onClick={onRetry}
                data-testid="autosave-retry"
              >
                {t("autosave.retry")}
              </Button>
            )}
            {restored && onDiscard && (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0 text-muted-foreground underline"
                onClick={onDiscard}
                data-testid="autosave-discard"
              >
                {t("autosave.discard")}
              </Button>
            )}
          </span>
        );
      case "offline":
        return (
          <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
            <CloudOff className="h-4 w-4" aria-hidden />
            {t("autosave.offline")}
          </span>
        );
      default:
        return null;
    }
  })();

  return (
    <div className={cn("flex flex-wrap items-center gap-2 text-sm", className)}>
      <span data-testid="autosave-status" data-status={status} aria-live="polite" role="status" className="empty:hidden">
        {chip}
      </span>
      {onSaveNow && (
        <Button
          type="button"
          variant="outline"
          onClick={onSaveNow}
          disabled={status === "saving"}
          data-testid="autosave-save-now"
        >
          <Save className="mr-2 h-4 w-4" aria-hidden />
          {t("autosave.saveNow")}
        </Button>
      )}
    </div>
  );
}
